import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSeo } from '../../lib/i18n'

export default function LovpakkePriserEn() {
  useSeo({
    title: 'Mandatory Packages & Prices | Borgs Driving School Kalundborg',
    description: 'Explore mandatory package prices at Borgs Driving School in Kalundborg. First-time driver license Cat. B, trailer licence B/E, licence recovery, and driving bans.',
    lang: 'en',
    pathname: '/en/mandatory-package-prices'
  })

  const [activeTab, setActiveTab] = useState('all')

  const detailedPackages = [
    {
      id: 'first-time-licence',
      title: 'First-time Driving Licence',
      subtitle: 'Package offer for first-time driving licence for standard passenger car (Cat. B)',
      price: '16.960 kr.',
      signupUrl: 'https://gondrive.com/api/site/school/96/iframe/3?license=B',
      popular: true,
      features: [
        'Theory course',
        '17 mandatory driving lessons on the road*',
        'Manoeuvre track',
        'Technical skid track'
      ],
      footnotes: [
        '*Please note that statutory lessons are legal minimums and may not always suffice to pass the practical test.',
        'All prices include VAT.'
      ],
      notIncludedTitle: 'The following is NOT included in the package but required to obtain a licence:',
      notIncluded: [
        'Medical certificate (typically 300–500 DKK, paid directly to your own doctor)',
        'First aid certificate (approx. 800 DKK)',
        'Official test fee to Danish Road Safety Agency (approx. 1,600 DKK)',
        'Driving licence photo: 100 DKK',
        'Manoeuvre track administration fee',
        'Car rental for the practical driving test (565 DKK)'
      ]
    },
    {
      id: 'recovery',
      title: 'Licence Recovery',
      subtitle: 'Package: controlling driving test & licence recovery',
      contentHeading: 'Package Contents',
      description: 'Offer for drivers with 3 penalty points or a conditional revocation who have held a licence for more than 3 years.',
      price: '2.500 kr.',
      signupUrl: '/en/contact',
      popular: false,
      features: [
        'Theory course on PC with home online access*',
        '2 practical driving lessons on the road*',
        'Car hire for the practical driving test'
      ],
      footnotes: [
        '*Please note that practical lessons may need supplementing depending on individual driving habits.',
        'All prices include VAT.',
        'There is no strict statutory package for licence recovery, but this represents an ideal foundation for passing theory and practical tests for experienced drivers.'
      ],
      notIncludedTitle: 'The following is NOT included in the package but required to recover your licence:',
      notIncluded: [
        'A/T Alcohol & Traffic Course (if required, 3,200 DKK)',
        'Driving licence photo: 100 DKK',
        'Official test fee to Danish Road Safety Agency (1,690 DKK)'
      ]
    },
    {
      id: 'trailer-licence',
      title: 'Trailer Licence (B/E)',
      subtitle: 'Package for trailer driving licence',
      price: '5.790 kr.',
      signupUrl: 'https://gondrive.com/api/site/school/96/iframe/3?license=B/E',
      popular: false,
      customContact: {
        text: 'Questions? Call Kurt 40 50 71 54',
        phone: '40507154',
        label: 'Kurt 40 50 71 54'
      },
      features: [
        'Theory course (4 lessons)',
        '6 mandatory driving lessons on the road'
      ],
      footnotes: [
        'All prices include VAT.'
      ],
      notIncludedTitle: 'The following is NOT included in the package but required to obtain a trailer licence:',
      notIncluded: [
        'Medical certificate (typically 300–500 DKK, paid to own doctor)',
        'Official test fee: 520 DKK',
        'Driving licence photo: from 100 DKK'
      ]
    },
    {
      id: 'driving-ban',
      title: 'Driving Ban Special Course',
      subtitle: 'Special education required after a driving ban (kørselsforbud)',
      description: 'Offer for new drivers with 2 penalty points within their first 3 years or stopped by the police resulting in a temporary ban.',
      price: '5.700 kr.',
      signupUrl: '/en/contact',
      popular: false,
      features: [
        'Theory course (8 statutory lessons)*',
        '8 mandatory driving lessons on the road*'
      ],
      footnotes: [
        '*Note: The statutory lessons are mandatory minimum requirements.',
        '*Note: Theory course includes complete online access to extra modules and sample tests.',
        'All prices include VAT.'
      ],
      notIncludedTitle: 'The following is NOT included in the package but required to regain licence:',
      notIncluded: [
        'Car hire for the practical test: 565 DKK',
        'Driving licence photo: 100 DKK',
        'Official test fee: 1,690 DKK'
      ]
    },
    {
      id: 'motorcycle',
      title: 'Motorcycle Classes',
      subtitle: 'Motorcycle instruction (Cat. A)',
      price: 'Not available',
      signupUrl: '/en/contact',
      popular: false,
      features: [],
      noticeText: 'Unfortunately, motorcycle courses cannot be offered in 2026.',
      footnotes: [],
      notIncludedTitle: '',
      notIncluded: []
    }
  ]

  const filteredPackages = activeTab === 'all' 
    ? detailedPackages 
    : detailedPackages.filter(p => p.id === activeTab)

  return (
    <div className="bg-slate-50 min-h-screen py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Heading */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="font-header text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Mandatory Packages &amp; Prices
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            View all our startup packages, mandatory packages, and prices for driving licences in Kalundborg. We offer transparent pricing with zero hidden fees.
          </p>

          {/* Quick Filter Navigation Bar */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                activeTab === 'all'
                  ? 'bg-emerald-900 text-white shadow-md'
                  : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              All Packages
            </button>
            {detailedPackages.map((pkg) => (
              <button
                key={pkg.id}
                onClick={() => setActiveTab(pkg.id)}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer ${
                  activeTab === pkg.id
                    ? 'bg-emerald-900 text-white shadow-md'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {pkg.title}
              </button>
            ))}
          </div>
        </div>

        {/* Detailed Package Cards Grid */}
        <div className="space-y-12 mb-20">
          {filteredPackages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white rounded-2xl border-2 transition-all duration-300 shadow-md overflow-hidden ${
                pkg.popular
                  ? 'border-emerald-600 ring-2 ring-emerald-600/20'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {/* Card Header */}
              <div className="bg-slate-900 text-white p-6 sm:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-b border-slate-800">
                <div>
                  {pkg.popular && (
                    <span className="inline-block bg-[#8cc73d] text-slate-950 text-xs font-bold px-3 py-1 uppercase tracking-wider mb-2 rounded-full">
                      Most popular choice
                    </span>
                  )}
                  <h2 className="font-header text-2xl sm:text-3xl font-bold text-white">
                    {pkg.title}
                  </h2>
                  <p className="text-emerald-300 text-sm sm:text-base mt-1 font-medium">
                    {pkg.subtitle}
                  </p>
                  {pkg.description && (
                    <p className="text-slate-300 text-sm mt-2 max-w-2xl">
                      {pkg.description}
                    </p>
                  )}
                </div>

                {pkg.price && pkg.price !== 'Not available' && (
                  <div className="text-left md:text-right shrink-0">
                    <div className="text-3xl sm:text-4xl font-black text-[#8cc73d] font-header">
                      Pay only {pkg.price}
                    </div>
                    <span className="text-xs text-slate-400 block mt-1">All prices include VAT</span>
                  </div>
                )}
              </div>

              {/* Card Body */}
              <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* Left Column: Package contents */}
                <div>
                  <h3 className="font-header text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <i className="fa-solid fa-box-archive text-emerald-600" aria-hidden="true"></i>
                    <span>{pkg.contentHeading || 'Package Details'}</span>
                  </h3>

                  {pkg.noticeText ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-sm font-semibold">
                      <i className="fa-solid fa-triangle-exclamation mr-2 text-amber-600"></i>
                      {pkg.noticeText}
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {pkg.features.map((feat, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-3 text-slate-700 text-sm sm:text-base">
                          <i className="fa-solid fa-check text-emerald-600 mt-1 flex-shrink-0" aria-hidden="true"></i>
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Footnotes */}
                  {pkg.footnotes.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-slate-100 space-y-1.5 text-xs text-slate-500">
                      {pkg.footnotes.map((fn, fnIdx) => (
                        <p key={fnIdx}>{fn}</p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Column: Required extras not in base package */}
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 flex flex-col justify-between">
                  <div>
                    {pkg.notIncludedTitle && (
                      <>
                        <h4 className="font-header text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                          {pkg.notIncludedTitle}
                        </h4>
                        <ul className="space-y-2 mb-6">
                          {pkg.notIncluded.map((item, nIdx) => (
                            <li key={nIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-600">
                              <i className="fa-solid fa-plus text-slate-400 mt-0.5 flex-shrink-0 text-xs" aria-hidden="true"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>

                  <div className="pt-4 border-t border-slate-200/80">
                    {pkg.customContact && (
                      <p className="text-xs text-slate-600 mb-2 font-medium">
                        {pkg.customContact.text}
                      </p>
                    )}
                    {pkg.signupUrl.startsWith('http') ? (
                      <a
                        href={pkg.signupUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold text-sm sm:text-base transition-all shadow-sm active:scale-[0.98]"
                      >
                        <span>Sign up for this package</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
                      </a>
                    ) : (
                      <Link
                        to={pkg.signupUrl}
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm sm:text-base transition-all shadow-sm active:scale-[0.98]"
                      >
                        <span>Contact us for booking</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                      </Link>
                    )}
                  </div>
                </div>

              </div>
            </div>
          ))}
        </div>

        {/* Extra fees and statutory pricing info table */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10 shadow-sm mb-16">
          <h3 className="font-header text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <i className="fa-solid fa-receipt text-emerald-600" aria-hidden="true"></i>
            <span>Overview of Additional Fees &amp; Driving Lessons</span>
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-200 text-slate-900 font-bold text-xs uppercase tracking-wider bg-slate-50">
                  <th className="py-3 px-4">Service / Fee</th>
                  <th className="py-3 px-4">Price (incl. VAT)</th>
                  <th className="py-3 px-4">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                <tr className="hover:bg-slate-50/60 transition-colors">
                  <td className="py-3.5 px-4 font-semibold text-slate-900">Additional driving lesson (45 min)</td>
                  <td className="py-3.5 px-4 font-bold text-emerald-700">550 DKK</td>
                  <td className="py-3.5 px-4 text-xs text-slate-500">Per lesson beyond the statutory 16 lessons</td>
                </tr>
                <tr className="hover:bg-slate-50/60 transition-colors">
                  <td className="py-3.5 px-4 font-semibold text-slate-900">Practical test car rental</td>
                  <td className="py-3.5 px-4 font-bold text-emerald-700">565 DKK</td>
                  <td className="py-3.5 px-4 text-xs text-slate-500">Includes warm-up and car during the test</td>
                </tr>
                <tr className="hover:bg-slate-50/60 transition-colors">
                  <td className="py-3.5 px-4 font-semibold text-slate-900">Official test fee (Færdselsstyrelsen)</td>
                  <td className="py-3.5 px-4 font-bold text-emerald-700">1,600 DKK</td>
                  <td className="py-3.5 px-4 text-xs text-slate-500">Covers 1 theory test + 1 practical test (VAT exempt)</td>
                </tr>
                <tr className="hover:bg-slate-50/60 transition-colors">
                  <td className="py-3.5 px-4 font-semibold text-slate-900">A/T Course (Alcohol &amp; Traffic)</td>
                  <td className="py-3.5 px-4 font-bold text-emerald-700">3,200 DKK</td>
                  <td className="py-3.5 px-4 text-xs text-slate-500">Mandatory for alcohol-related revocations</td>
                </tr>
                <tr className="hover:bg-slate-50/60 transition-colors">
                  <td className="py-3.5 px-4 font-semibold text-slate-900">First Aid Course (Traffic-related)</td>
                  <td className="py-3.5 px-4 font-bold text-emerald-700">approx. 800 DKK</td>
                  <td className="py-3.5 px-4 text-xs text-slate-500">8 hours approved course</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA Banner */}
        <div className="bg-emerald-900 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6">
          <h2 className="font-header text-3xl sm:text-4xl font-extrabold">
            Ready to book your spot?
          </h2>
          <p className="max-w-2xl mx-auto text-emerald-100 text-base sm:text-lg">
            Sign up online or call us directly on 21 47 01 87 for questions about payment or team schedules.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
            <Link
              to="/en/contact"
              className="px-8 py-4 rounded-full bg-[#8cc73d] hover:bg-[#7eb832] text-slate-950 font-bold text-base transition-all"
            >
              Go to Contact &amp; Registration
            </Link>
            <a
              href="tel:21470187"
              className="px-8 py-4 rounded-full bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-base border border-emerald-600 transition-all flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
              <span>Call: 21 47 01 87</span>
            </a>
          </div>
        </div>

      </div>
    </div>
  )
}
